import { Fragment, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  TextField,
  MenuItem,
  Alert,
  Collapse,
  IconButton,
  Button,
  LinearProgress,
  Link,
} from '@mui/material';
import ForumIcon from '@mui/icons-material/ForumOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useMe } from '../api/hooks.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';
import { formatDateTime } from '../lib/format.js';

type Status = 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'WON' | 'CLOSED';
const STATUSES: Status[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'WON', 'CLOSED'];

/** NEW is the one that means "nobody has looked at this". */
const STATUS_COLOR: Record<Status, 'warning' | 'info' | 'primary' | 'success' | 'default'> = {
  NEW: 'warning',
  CONTACTED: 'info',
  QUALIFIED: 'primary',
  WON: 'success',
  CLOSED: 'default',
};

interface DemoRequest {
  id: string;
  name: string;
  email: string;
  company: string | null;
  website: string | null;
  teamSize: string | null;
  message: string | null;
  locale: string;
  source: string | null;
  status: Status;
  notes: string | null;
  createdAt: string;
  handledAt: string | null;
}

/**
 * The demo request queue.
 *
 * The point of the screen is the status column: a lead sitting at NEW is one
 * nobody has answered, and that is the only thing a form like this can tell
 * you that an inbox cannot.
 */
export function AdminDemoRequestsPage() {
  const all = useT();
  const t = all.admin.demoRequests;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const list = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-demo-requests', status, search],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (search) p.set('search', search);
      return (await api.get(`/v1/admin/demo-requests?${p}`)).data as {
        data: DemoRequest[];
        total: number;
        waiting: number;
      };
    },
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; status?: Status; notes?: string }) =>
      api.patch(`/v1/admin/demo-requests/${input.id}`, {
        ...(input.status ? { status: input.status } : {}),
        ...(input.notes !== undefined ? { notes: input.notes } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-demo-requests'] }),
  });

  const sort = useSort<'who' | 'company' | 'team' | 'status' | 'received'>('received');
  const rows = sort.sorted(list.data?.data ?? [], {
    who: (r) => r.name,
    company: (r) => r.company,
    team: (r) => r.teamSize,
    status: (r) => STATUSES.indexOf(r.status),
    received: (r) => r.createdAt,
  });

  if (me && !me.isSuperAdmin) return <Alert severity="error">{needSuperAdmin}</Alert>;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ForumIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
        {!!list.data?.waiting && (
          <Chip size="small" color="warning" label={t.waiting(list.data.waiting)} sx={{ fontWeight: 700 }} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        {t.subtitle}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          select
          size="small"
          label={t.status}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t.anyStatus}</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {t.statuses[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label={t.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      <Paper sx={{ overflow: 'hidden' }}>
        {list.isLoading && <LinearProgress />}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                {sort.head('who', t.columns.who)}
                {sort.head('company', t.columns.company)}
                {sort.head('team', t.columns.team)}
                {sort.head('status', t.columns.status)}
                {sort.head('received', t.columns.received)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !list.isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t.empty}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setOpen(open === r.id ? null : r.id);
                          setNoteDraft(r.notes ?? '');
                        }}
                        sx={{ transform: open === r.id ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {r.name}
                      </Typography>
                      <Link href={`mailto:${r.email}`} variant="caption" underline="hover">
                        {r.email}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.company ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {r.teamSize ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={r.status}
                        onChange={(e) => update.mutate({ id: r.id, status: e.target.value as Status })}
                        sx={{ minWidth: 140 }}
                        SelectProps={{
                          renderValue: (v) => (
                            <Chip size="small" color={STATUS_COLOR[v as Status]} label={t.statuses[v as Status]} />
                          ),
                        }}
                      >
                        {STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>
                            {t.statuses[s]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(r.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                      <Collapse in={open === r.id} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                          {r.message && (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                {t.theirMessage}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                                {r.message}
                              </Typography>
                            </>
                          )}
                          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                            {r.website && (
                              <Typography variant="caption" color="text.secondary">
                                {t.website}: {r.website}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {t.language}: {r.locale.toUpperCase()}
                            </Typography>
                            {r.source && (
                              <Typography variant="caption" color="text.secondary">
                                {t.source}: {r.source}
                              </Typography>
                            )}
                            {r.handledAt && (
                              <Typography variant="caption" color="text.secondary">
                                {t.handled}: {formatDateTime(r.handledAt)}
                              </Typography>
                            )}
                          </Stack>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            size="small"
                            label={t.notes}
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                          />
                          <Button
                            size="small"
                            sx={{ mt: 1 }}
                            disabled={update.isPending || noteDraft === (r.notes ?? '')}
                            onClick={() => update.mutate({ id: r.id, notes: noteDraft })}
                          >
                            {t.saveNote}
                          </Button>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
